import { useCallback, useMemo, useEffect, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  useReactFlow,
  useUpdateNodeInternals,
  Panel,
  type Connection,
  type OnConnect,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Plus,
  Minus,
  Maximize2,
  ArrowRightFromLine,
  ArrowDownFromLine,
  FlaskConical,
  Settings,
  ChevronLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";

import type {
  DecisionDefinition,
  DecisionEditorState,
  EditorNodeState,
  EmitRule,
} from "@/types/decision-definition-schema";
import { generateNodeId } from "@/types/decision-definition-schema";
import type { CaseContextField } from "@/types/decision-editor";
import type {
  ExpressionNode,
  ExpressionEdge,
  ExpressionNodeData,
  HubNodeData,
  LayoutDirection,
} from "@/types/decision-graph";
import {
  convertDefinitionToGraph,
  exportGraphToDefinition,
} from "@/lib/tree-flow-converter";
import { FlowProvider, getLayoutedElements } from "@/components/react-flow";
import { nodeTypes } from "./nodes";
import { edgeTypes } from "./edges";
import { DecisionToolbox, useDecisionToolbox } from "./DecisionToolbox";
import { DecisionNodeDrawer } from "./DecisionNodeDrawer";
import { DecisionTestDrawer } from "./DecisionTestDrawer";
import { DecisionSettingsDrawer } from "./DecisionSettingsDrawer";
import {
  validateConnection,
  wouldCreateCycle,
  validateGraph,
} from "./utils/expression-validator";
import {
  getHubOutputHandles,
  resolveEmitSourceHandle,
  sortEmitRulesForLayout,
} from "./utils/hub-handles";

// =============================================================================
// Props
// =============================================================================

interface DecisionGraphProps {
  definition: DecisionDefinition;
  editorState?: DecisionEditorState;
  onChange?: (definition: DecisionDefinition, editorState?: DecisionEditorState) => void;
  readOnly?: boolean;
  caseContextSchema?: CaseContextField[];
  decisionId?: string;
}

// =============================================================================
// Layout Config
// =============================================================================

const LAYOUT_CONFIG = {
  direction: "LR" as const,
  nodeSeparation: 50,
  rankSeparation: 180,
};

// =============================================================================
// Inner Component (needs ReactFlow context)
// =============================================================================

function DecisionGraphInner({
  definition,
  editorState,
  onChange,
  readOnly = false,
  caseContextSchema = [],
  decisionId = "",
}: DecisionGraphProps) {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const definitionRef = useRef(definition);
  definitionRef.current = definition;

  const [isUpdatingFromProp, setIsUpdatingFromProp] = useState(false);
  const lastDefinitionRef = useRef<DecisionDefinition | null>(null);

  // Drawer states
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isNodeDrawerOpen, setIsNodeDrawerOpen] = useState(false);
  const [isTestDrawerOpen, setIsTestDrawerOpen] = useState(false);
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>("LR");

  // Toolbox
  const toolbox = useDecisionToolbox();
  const handleAutoLayoutRef = useRef<((direction?: LayoutDirection) => void) | null>(null);

  // Convert definition to graph on initial load
  const { initialNodes, initialEdges } = useMemo(() => {
    const nodePositions = editorState?.nodes;
    const result = convertDefinitionToGraph(definition, nodePositions);
    return { initialNodes: result.nodes, initialEdges: result.edges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as any[]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges as any[]);

  // Sync from prop changes
  useEffect(() => {
    if (lastDefinitionRef.current === definition) return;

    setIsUpdatingFromProp(true);
    lastDefinitionRef.current = definition;

    const nodePositions = editorState?.nodes;
    const result = convertDefinitionToGraph(definition, nodePositions);

    setNodes(result.nodes as any[]);
    setEdges(result.edges as any[]);

    setTimeout(() => setIsUpdatingFromProp(false), 100);
  }, [definition, editorState, setNodes, setEdges]);

  // Export changes back to definition
  useEffect(() => {
    if (isUpdatingFromProp || readOnly) return;

    const validation = validateGraph(
      nodes as unknown as ExpressionNode[],
      edges as unknown as ExpressionEdge[]
    );

    if (validation.valid && onChangeRef.current) {
      const updatedDefinition = exportGraphToDefinition(
        nodes as unknown as ExpressionNode[],
        edges as unknown as ExpressionEdge[],
        definitionRef.current
      );

      // Build editor state from node positions
      const newEditorState: DecisionEditorState = {
        layoutVersion: 1,
        viewport: { x: 0, y: 0, zoom: 1 },
        nodes: {},
        groups: [],
      };
      for (const node of nodes) {
        newEditorState.nodes[node.id] = {
          x: node.position.x,
          y: node.position.y,
        };
      }

      lastDefinitionRef.current = updatedDefinition;
      onChangeRef.current(updatedDefinition, newEditorState);
    }
  }, [nodes, edges, isUpdatingFromProp, readOnly]);

  // Connection validation
  const isValidConnection = useCallback(
    (connection: Connection | { source: string; target: string; sourceHandle?: string | null; targetHandle?: string | null }): boolean => {
      const conn = connection as Connection;
      const validation = validateConnection(
        conn,
        nodes as unknown as ExpressionNode[],
        edges as unknown as ExpressionEdge[]
      );
      if (!validation.valid) return false;
      if (wouldCreateCycle(conn, edges as unknown as ExpressionEdge[]))
        return false;
      return true;
    },
    [nodes, edges]
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (readOnly || !isValidConnection(connection)) return;
      setEdges((eds) =>
        addEdge(
          { ...connection, type: "expression", data: { edgeType: "value" } },
          eds
        )
      );
    },
    [setEdges, isValidConnection, readOnly]
  );

  // Drag-and-drop from toolbox
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (readOnly) return;

      const type = event.dataTransfer.getData("application/reactflow-type");
      if (!type) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = createNodeOfType(type, position, caseContextSchema, layoutDirection);
      if (newNode) {
        setNodes((nds) => [...nds, newNode as Node]);
      }
    },
    [reactFlowInstance, readOnly, caseContextSchema, setNodes, layoutDirection]
  );

  // Auto-layout
  const { fitView, zoomIn, zoomOut } = useReactFlow();

  const handleAutoLayout = useCallback((direction?: LayoutDirection) => {
    const dir = direction ?? layoutDirection;
    const { nodes: layoutedNodes } = getLayoutedElements(nodes, edges, {
      ...LAYOUT_CONFIG,
      direction: dir,
    });
    const withDirection = layoutedNodes.map((n) => ({
      ...n,
      data: { ...n.data, layoutDirection: dir },
    }));
    setNodes(withDirection as Node[]);

    // Notify React Flow that handles moved so edges reconnect correctly
    setTimeout(() => {
      withDirection.forEach((n) => updateNodeInternals(n.id));
      fitView({ padding: 0.2, duration: 300 });
    }, 50);
  }, [nodes, edges, setNodes, fitView, layoutDirection, updateNodeInternals]);

  handleAutoLayoutRef.current = handleAutoLayout;

  // Node click → open drawer
  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id);
      setIsNodeDrawerOpen(true);
    },
    []
  );

  // Selected node object
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    return (nodes.find((n) => n.id === selectedNodeId) as unknown as ExpressionNode) || null;
  }, [nodes, selectedNodeId]);

  // Node data change handler (from drawer)
  const handleNodeDataChange = useCallback(
    (nodeId: string, updates: Partial<ExpressionNodeData>) => {
      const modeUpdate =
        "decisionMode" in updates
          ? (updates as { decisionMode?: HubNodeData["decisionMode"] }).decisionMode
          : undefined;
      const bandsUpdate =
        "scoreBands" in updates
          ? (updates as { scoreBands?: HubNodeData["scoreBands"] }).scoreBands
          : undefined;

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== nodeId) return n;
          const nextData = { ...n.data, ...updates };
          if (n.data.nodeType === "hub") {
            (nextData as HubNodeData).emitRules = definitionRef.current.emitRules || [];
          }
          return { ...n, data: nextData };
        })
      );

      // Persist scoreBands to definition
      if (bandsUpdate !== undefined && onChangeRef.current) {
        onChangeRef.current(
          { ...definitionRef.current, scoreBands: bandsUpdate || [] },
          undefined
        );
        setTimeout(() => updateNodeInternals(nodeId), 0);
      }

      if (modeUpdate && onChangeRef.current) {
        onChangeRef.current(
          {
            ...definitionRef.current,
            metadata: {
              ...definitionRef.current.metadata,
              mode: modeUpdate,
            },
          },
          undefined
        );

        // Rewire hub → emit edges to mode-appropriate source handles
        setTimeout(() => {
          const ordered = sortEmitRulesForLayout(
            definitionRef.current.emitRules || []
          );
          const handles = getHubOutputHandles(
            modeUpdate,
            ordered,
            definitionRef.current.scoreBands || []
          );
          setEdges((eds) =>
            eds.map((e) => {
              if (e.source !== "__hub__") return e;
              const emitId = String(e.target)
                .replace(/^__emit_/, "")
                .replace(/__$/, "");
              const idx = ordered.findIndex((r) => r.id === emitId);
              if (idx < 0) {
                return {
                  ...e,
                  sourceHandle: handles[0]?.id ?? e.sourceHandle,
                };
              }
              return {
                ...e,
                sourceHandle: resolveEmitSourceHandle(
                  modeUpdate,
                  ordered[idx],
                  idx,
                  handles
                ),
              };
            })
          );
          updateNodeInternals(nodeId);
        }, 0);
      }
    },
    [setNodes, setEdges, updateNodeInternals]
  );

  // Emit rule change handler
  const handleEmitRuleChange = useCallback(
    (emitRuleId: string, updates: Partial<EmitRule>) => {
      if (!onChangeRef.current) return;
      const updatedRules = (definitionRef.current.emitRules || []).map((r) =>
        r.id === emitRuleId ? { ...r, ...updates } : r
      );
      onChangeRef.current(
        { ...definitionRef.current, emitRules: updatedRules },
        undefined
      );
    },
    []
  );

  // Definition change from settings drawer
  const handleDefinitionChange = useCallback(
    (updatedDef: DecisionDefinition) => {
      if (onChangeRef.current) {
        onChangeRef.current(updatedDef, undefined);
      }
    },
    []
  );

  // Decision JSON for tester
  const decisionJson = useMemo(
    () => JSON.stringify({ definition }, null, 2),
    [definition]
  );

  return (
    <div className="relative flex h-full w-full flex-col">
      <div className="relative flex min-h-0 flex-1">
        {/* Main graph area */}
        <div className="relative min-w-0 flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onConnect={onConnect}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            isValidConnection={isValidConnection as any}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            elementsSelectable={true}
            onDragOver={onDragOver}
            onDrop={onDrop}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.1}
            defaultEdgeOptions={{ type: "expression" }}
            proOptions={{ hideAttribution: true }}
            className="bg-slate-50"
            deleteKeyCode={null}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#cbd5e1" />
            <MiniMap
              className="border-slate-200! bg-white!"
              zoomable
              pannable
              nodeStrokeWidth={3}
            />

            {/* Toolbar Panel */}
            <Panel position="top-left" className="flex items-center gap-2">
              <div className="flex items-center rounded-md border border-slate-200 bg-white shadow-sm">
                <button
                  onClick={() => zoomIn()}
                  title="Zoom in"
                  className="flex h-7 w-7 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <Plus size={14} />
                </button>
                <button
                  onClick={() => zoomOut()}
                  title="Zoom out"
                  className="flex h-7 w-7 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <Minus size={14} />
                </button>
                <button
                  onClick={() => fitView({ padding: 0.2 })}
                  title="Fit view"
                  className="flex h-7 w-7 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  <Maximize2 size={14} />
                </button>
                <button
                  onClick={() => {
                    setLayoutDirection("LR");
                    setTimeout(() => handleAutoLayoutRef.current?.("LR"), 0);
                  }}
                  title="Horizontal layout"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50",
                    layoutDirection === "LR"
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-600"
                  )}
                >
                  <ArrowRightFromLine size={14} />
                </button>
                <button
                  onClick={() => {
                    setLayoutDirection("TB");
                    setTimeout(() => handleAutoLayoutRef.current?.("TB"), 0);
                  }}
                  title="Vertical layout"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50",
                    layoutDirection === "TB"
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-600"
                  )}
                >
                  <ArrowDownFromLine size={14} />
                </button>
                <button
                  onClick={() => setIsTestDrawerOpen(true)}
                  title="Test decision"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50",
                    isTestDrawerOpen
                      ? "bg-green-50 text-green-600"
                      : "text-slate-600"
                  )}
                >
                  <FlaskConical size={14} />
                </button>
                <button
                  onClick={() => setIsSettingsDrawerOpen(true)}
                  title="Decision settings"
                  className="flex h-7 w-7 items-center justify-center text-slate-600 hover:bg-slate-50"
                >
                  <Settings size={14} />
                </button>
              </div>
            </Panel>

            {/* Read-only indicator */}
            {readOnly && (
              <Panel position="top-right">
                <div className="bg-muted/80 px-2 py-1 rounded text-xs text-muted-foreground">
                  Read-only
                </div>
              </Panel>
            )}
          </ReactFlow>

          {/* Hover zone for toolbox (right edge) — only when not pinned/open */}
          {!readOnly && !toolbox.isPinned && !toolbox.isOpen && (
            <div
              className="absolute right-0 top-0 z-10 h-full w-8 cursor-pointer bg-linear-to-l from-slate-200/60 to-transparent transition-all duration-200 hover:w-10 hover:from-slate-300/80"
              onMouseEnter={toolbox.onMouseEnter}
            >
              <div className="absolute right-0 top-1/3 flex h-16 w-6 items-center justify-center rounded-l-md bg-slate-300/70 transition-colors hover:bg-slate-400/80">
                <ChevronLeft size={16} className="text-slate-600" />
              </div>
            </div>
          )}
        </div>

        {/* Toolbox sits beside the graph when pinned; overlays when hovering */}
        {!readOnly && (
          <div className="relative h-full">
            <DecisionToolbox
              isOpen={toolbox.isOpen}
              onClose={toolbox.onClose}
              isPinned={toolbox.isPinned}
              onPinToggle={toolbox.onPinToggle}
              onMouseEnter={toolbox.onMouseEnter}
              onMouseLeave={toolbox.onMouseLeave}
            />
          </div>
        )}
      </div>

      {/* Node detail drawer */}
      <DecisionNodeDrawer
        isOpen={isNodeDrawerOpen}
        onClose={() => {
          setIsNodeDrawerOpen(false);
          setSelectedNodeId(null);
        }}
        selectedNode={selectedNode}
        definition={definition}
        nodes={nodes as unknown as ExpressionNode[]}
        edges={edges as unknown as ExpressionEdge[]}
        onNodeDataChange={handleNodeDataChange}
        onEmitRuleChange={handleEmitRuleChange}
        onDefinitionChange={handleDefinitionChange}
        readOnly={readOnly}
      />

      {/* Test drawer */}
      <DecisionTestDrawer
        isOpen={isTestDrawerOpen}
        onClose={() => setIsTestDrawerOpen(false)}
        decisionId={decisionId}
        decisionJson={decisionJson}
        caseContextSchema={caseContextSchema}
      />

      {/* Settings drawer */}
      <DecisionSettingsDrawer
        isOpen={isSettingsDrawerOpen}
        onClose={() => setIsSettingsDrawerOpen(false)}
        definition={definition}
        onChange={handleDefinitionChange}
        readOnly={readOnly}
      />
    </div>
  );
}

// =============================================================================
// Node Factory (for drag-and-drop from toolbox)
// =============================================================================

function createNodeOfType(
  type: string,
  position: { x: number; y: number },
  caseContextSchema: CaseContextField[],
  layoutDirection: LayoutDirection = "LR"
): ExpressionNode | null {
  const id = `expr-${Date.now()}`;

  switch (type) {
    case "literal":
      return {
        id,
        type: "literal",
        position,
        data: {
          nodeType: "literal",
          label: "Literal",
          value: "",
          valueType: "string",
          layoutDirection,
        },
      };

    case "field":
      return {
        id,
        type: "field",
        position,
        data: {
          nodeType: "field",
          label: "Fact",
          path: caseContextSchema[0]?.path || "",
          fieldType: "string",
          layoutDirection,
        },
      };

    case "comparison":
      return {
        id,
        type: "comparison",
        position,
        data: {
          nodeType: "comparison",
          label: "==",
          operator: "==",
          layoutDirection,
        },
      };

    case "binary_and":
      return {
        id,
        type: "binary",
        position,
        data: {
          nodeType: "binary",
          label: "AND",
          operator: "AND",
          layoutDirection,
        },
      };

    case "binary_or":
      return {
        id,
        type: "binary",
        position,
        data: {
          nodeType: "binary",
          label: "OR",
          operator: "OR",
          layoutDirection,
        },
      };

    case "unary":
      return {
        id,
        type: "unary",
        position,
        data: {
          nodeType: "unary",
          label: "NOT",
          operator: "NOT",
          layoutDirection,
        },
      };

    case "existence":
      return {
        id,
        type: "existence",
        position,
        data: {
          nodeType: "existence",
          label: "EXISTS",
          operator: "EXISTS",
          layoutDirection,
        },
      };

    case "membership":
      return {
        id,
        type: "membership",
        position,
        data: {
          nodeType: "membership",
          label: "IN",
          operator: "IN",
          setSize: 0,
          layoutDirection,
        },
      };

    case "emitOutput":
      return {
        id: `__emit_${generateNodeId()}__`,
        type: "emitOutput",
        position,
        data: {
          nodeType: "emitOutput",
          label: "New Output",
          emitRuleId: generateNodeId(),
          outcome: "",
          stopProcessing: false,
          layoutDirection,
        },
      } as unknown as ExpressionNode;

    default:
      return null;
  }
}

// =============================================================================
// Public Component (wraps with FlowProvider)
// =============================================================================

export function DecisionGraph(props: DecisionGraphProps) {
  return (
    <FlowProvider>
      <DecisionGraphInner {...props} />
    </FlowProvider>
  );
}

export default DecisionGraph;
